// src/components/layout/Header.tsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@/context/NavigationContext';
import { useAuthContext } from '@/context/AuthContext';
import { Menu, LogOut, Bell, Globe } from 'lucide-react';
import Logo from '@/components/common/Logo';
import AuthModal from '@/components/auth/AuthModal';
import UserProfileDropdown from '@/components/user/UserProfileDropdown';
import Message from '@/components/ui/Message';
import { useResponsive } from '@/hooks/useResponsive';
import SidebarMenu from '@/components/layout/SidebarMenu';
import styles from '@/styles/layout/Header.module.css';

interface HeaderProps {
  onToggleSidebar?: () => void; // Колбэк для переключения боковой панели 
  onNotifications?: () => void; // Колбэк для уведомлений (для мобильного интерфейса)
  title?: string; // Заголовок для мобильной версии
}

const Header: React.FC<HeaderProps> = ({ 
  onNotifications
}) => {
  const { t, i18n } = useTranslation('translation');
  const { navigateTo } = useNavigation();
  const { isAuthenticated, logout, user } = useAuthContext();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalView, setAuthModalView] = useState<'login' | 'register'>('login');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  
  // Используем хук для получения информации о размерах экрана и ориентации
  const responsive = useResponsive();
  const { isMobile, isTablet, isLandscape } = responsive;

  // Определяем, нужно ли показывать кнопку меню
  const showMenuButton = isAuthenticated;
  
  // Определяем, нужно ли показывать компактный пользовательский интерфейс
  const isCompact = isMobile || (isTablet && isLandscape);
  
  // Определяем доступные языки и текущий язык
  const languages = [
    { code: 'ru', name: t('language.ru') },
    { code: 'en', name: t('language.en') }
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];
  
  const openLoginModal = () => {
    setAuthModalView('login');
    setAuthModalOpen(true);
  };

  const openRegisterModal = () => {
    setAuthModalView('register');
    setAuthModalOpen(true);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigateTo('home');
    } catch (error) {
      console.error("Error during logout:", error);
      setErrorMessage('auth.errors.general');
      navigateTo('home');
    }
  };

  // Обработчик открытия/закрытия бокового меню
  const handleToggleSidebar = () => {
    setSidebarOpen(prev => !prev);
  };

  // Обработчики событий с проверкой наличия пропсов
  const handleNotifications = () => {
    if (onNotifications) {
      onNotifications();
    } else {
      console.log('Открыть уведомления');
      // Здесь будет логика обработки уведомлений по умолчанию
    }
  };

  // Обработчик для переключения языка
  const handleLanguageChange = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setIsLanguageMenuOpen(false);
  };

  return (
    <>
      <nav className={styles.header}>
        <div className={styles.container}>
          <div className={styles.content}>
            {/* Левая часть: кнопка меню и лого */}
            <div className={styles.leftSection}>
              {showMenuButton && (
                <button 
                  onClick={handleToggleSidebar}
                  className={styles.menuButton}
                  aria-label={t('navigation.toggle_menu')}
                >
                  <Menu className={isCompact ? 'w-5 h-5' : 'w-6 h-6'} />
                </button>
              )}
              <div 
                className={styles.logoContainer}
                onClick={() => navigateTo(isAuthenticated ? 'personal' : 'home')}
              >
                <Logo className={styles.logo} />
              </div>
            </div>
            
            {/* Правая часть: кнопки или профиль пользователя */}
            <div className={styles.rightSection}>
              {isAuthenticated ? (
                // Авторизованное состояние с выпадающим меню или компактными иконками
                <div className={styles.userInfoContainer}>
                  {isCompact ? (
                    // На мобильных показываем иконки переключения языка, уведомлений и выхода
                    <div className="flex items-center space-x-3">
                      {/* Кнопка переключения языка */}
                      <div className="relative">
                        <button 
                          className="p-1 rounded-full hover:bg-gray-100"
                          onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
                          aria-label={t('language.change', 'Изменить язык')}
                        >
                          <Globe className="w-5 h-5 text-gray-700" />
                        </button>
                        
                        {/* Выпадающее меню выбора языка */}
                        {isLanguageMenuOpen && (
                          <div className="absolute right-0 mt-1 bg-white rounded-md shadow-lg z-50 min-w-[100px]">
                            {languages.map(lang => (
                              <button
                                key={lang.code}
                                onClick={() => handleLanguageChange(lang.code)}
                                className={`
                                  w-full text-left px-3 py-2 text-sm hover:bg-gray-100
                                  ${currentLanguage.code === lang.code ? 'bg-gray-50 font-medium text-[#245D33]' : 'text-gray-700'}
                                `}
                                aria-selected={currentLanguage.code === lang.code}
                              >
                                {lang.name}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Кнопка уведомлений */}
                      <button 
                        className="p-1 rounded-full hover:bg-gray-100"
                        onClick={handleNotifications}
                        aria-label={t('notifications', 'Уведомления')}
                      >
                        <Bell className="w-5 h-5 text-gray-700" />
                      </button>
                      
                      {/* Кнопка выхода */}
                      <button
                        onClick={handleLogout}
                        className={styles.logoutButton}
                        aria-label={t('auth.logout', 'Выйти')}
                      >
                        <LogOut className={styles.logoutIcon} />
                      </button>
                    </div>
                  ) : (
                    // На десктопах полный дропдаун
                    user && <UserProfileDropdown user={user} onLogout={handleLogout} />
                  )}
                </div>
              ) : (
                // Неавторизованное состояние - кнопки входа и регистрации
                <div className={styles.authButtons}>
                  <button 
                    onClick={openLoginModal}
                    className={`${styles.loginButton} ${isCompact ? styles.loginButtonCompact : styles.loginButtonRegular}`}
                  >
                    {t('navbar.login', 'Войти')}
                  </button>
                  <button 
                    onClick={openRegisterModal}
                    className={`${styles.registerButton} ${isCompact ? styles.registerButtonCompact : styles.registerButtonRegular}`}
                  >
                    {isCompact ? t('navbar.register_short', 'Регистрация') : t('navbar.register', 'Регистрация')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>
      
      {errorMessage && (
        <div className={styles.messageContainer}>
          <Message 
            type="error" 
            translateKey={errorMessage} 
            dismiss={() => setErrorMessage(null)}
          />
        </div>
      )}
      
      {/* Боковое меню */}
      <SidebarMenu 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)} 
      />
      
      {/* Модальное окно аутентификации */}
      <AuthModal 
        isOpen={authModalOpen} 
        onClose={() => setAuthModalOpen(false)} 
        initialView={authModalView}
      />
    </>
  );
};

export default Header;